/**
 * DataMgmtPanel — Data export, privacy settings, and danger zone.
 *
 * Export projects in various formats, configure data retention and
 * crystallization consent, and destructive account operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api/engine';
import { SettingsSection, SliderSetting, ToggleSetting, DangerButton } from '@seitiate/settings-ui';
import { BackupPanel } from './BackupPanel';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface PrivacySettings {
    data_retention_days: number;
    crystallization_consent: boolean;
}

interface DeletionStatus {
    status: string;
    scheduled_for?: string;
    grace_period_days?: number;
}

export const DataMgmtPanel: React.FC<{ mode: 'admin' | 'tenant' }> = () => {
    const [privacy, setPrivacy] = useState<PrivacySettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
    const [exportChoice, setExportChoice] = useState<'download' | 'skip' | null>(null);
    const [deletionBusy, setDeletionBusy] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.settings.getCategory('privacy');
            setPrivacy(response.settings as unknown as PrivacySettings);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        fetch(`${API_V1}/account/delete/status`, { headers: buildEngineHeaders() })
            .then((r) => r.json())
            .then((data) => setDeletionStatus(data))
            .catch(() => {});
    }, []);

    const updatePrivacy = async (key: string, value: number | boolean) => {
        if (!privacy) return;
        setPrivacy({ ...privacy, [key]: value });
        try {
            const response = await api.settings.updateCategory('privacy', { [key]: value });
            setPrivacy(response.settings as unknown as PrivacySettings);
        } catch {
            setFeedback('Failed to save');
        }
    };

    const ensureDeletionRequest = async () => {
        if (deletionStatus?.status === 'pending' || deletionStatus?.status === 'failed') return;
        const response = await fetch(`${API_V1}/account/delete`, {
            method: 'POST', headers: buildEngineHeaders(), body: JSON.stringify({}),
        });
        if (!response.ok) throw new Error('Failed to request deletion. Please try again.');
        setDeletionStatus(await response.json());
    };

    const downloadBeforeDeletion = async () => {
        setFeedback(null);
        setExportChoice(null);
        setDeletionBusy(true);
        try {
            await ensureDeletionRequest();
            const response = await fetch(`${API_V1}/account/delete/export`, { headers: buildEngineHeaders() });
            if (!response.ok) throw new Error('Download failed. Try again, or choose Skip.');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            try {
                const link = document.createElement('a');
                link.href = url;
                link.download = 'seity-tenant-bundle.json';
                document.body.appendChild(link);
                try { link.click(); } finally { link.remove(); }
            } finally {
                URL.revokeObjectURL(url);
            }
            setExportChoice('download');
            setFeedback('Download started. Keep your data before confirming deletion.');
        } catch (err) {
            setFeedback(err instanceof Error ? err.message : 'Download failed. Try again, or choose Skip.');
        } finally {
            setDeletionBusy(false);
        }
    };

    const requestDeletion = async () => {
        if (!exportChoice || deletionBusy) return;
        setFeedback(null);
        setDeletionBusy(true);
        try {
            await ensureDeletionRequest();
            const confirmResp = await fetch(`${API_V1}/account/delete/confirm?export_choice=${exportChoice}`, {
                method: 'POST', headers: buildEngineHeaders(),
            });
            if (!confirmResp.ok) {
                setFeedback('Deletion request created but confirmation failed.');
                return;
            }
            const data = await confirmResp.json();
            setDeletionStatus(data);
            setFeedback(data.status === 'completed' ? 'Account deleted.' : `Account deletion is ${data.status}.`);
        } catch (err) {
            setFeedback(err instanceof Error ? err.message : 'Failed to request deletion. Please try again.');
        } finally {
            setDeletionBusy(false);
        }
    };

    const cancelDeletion = async () => {
        setFeedback(null);
        setDeletionBusy(true);
        try {
            const resp = await fetch(`${API_V1}/account/delete/cancel`, {
                method: 'POST',
                headers: buildEngineHeaders(),
            });
            if (!resp.ok) {
                setFeedback('Failed to cancel deletion. Please try again.');
                return;
            }
            const data = await resp.json();
            setDeletionStatus(data);
            setExportChoice(null);
            setFeedback('Deletion request cancelled. Your account is active again.');
        } catch {
            setFeedback('Failed to cancel deletion. Please try again.');
        } finally {
            setDeletionBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">Loading data management...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">{error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                Data Management
            </h3>

            {feedback && (
                <div className="text-[10px] font-mono text-amber-400">{feedback}</div>
            )}

            {(deletionStatus?.status === 'pending' || deletionStatus?.status === 'failed' || deletionStatus?.status === 'executing') && (
                <div className="rounded border border-red-800 bg-red-900/20 p-3">
                    <p className="text-[10px] font-mono text-red-300">
                        Account deletion is {deletionStatus.status}.
                        {deletionStatus.scheduled_for
                            ? ` Scheduled for ${new Date(deletionStatus.scheduled_for).toLocaleDateString()}.`
                            : ''}
                    </p>
                    {(deletionStatus.status === 'pending' || deletionStatus.status === 'failed') && (
                        <button disabled={deletionBusy} onClick={cancelDeletion} className="mt-1 text-[10px] text-red-200 underline">
                            Cancel deletion
                        </button>
                    )}
                </div>
            )}

            {/* Export */}
            <SettingsSection
                title="Export"
                badge="NOT YET AVAILABLE"
                badgeColor="text-[var(--theme-text-muted)]"
            >
                <p className="text-[10px]" style={{ color: 'var(--theme-text-muted)' }}>
                    Export your creative work in various formats.
                </p>
                <div className="flex flex-wrap gap-2 opacity-40">
                    {['Fountain (.fountain)', 'Manuscript (.docx)', 'Google Drive'].map((label) => (
                        <span
                            key={label}
                            className="px-3 py-1.5 text-xs font-mono rounded cursor-not-allowed"
                            style={{
                                border: '1px solid var(--theme-surface-tertiary)',
                                color: 'var(--theme-text-muted)',
                            }}
                        >
                            {label}
                        </span>
                    ))}
                </div>
                <p
                    className="text-[9px] font-mono mt-1"
                    style={{ color: 'var(--theme-text-muted)', opacity: 0.7 }}
                >
                    Project export is not yet available. Use Backup below to download your data.
                </p>
            </SettingsSection>

            <BackupPanel />

            {/* Privacy Settings */}
            {privacy && (
                <SettingsSection title="Privacy">
                    <SliderSetting
                        label="Data Retention"
                        value={privacy.data_retention_days}
                        min={30}
                        max={365}
                        step={30}
                        onChange={(v) => updatePrivacy('data_retention_days', v)}
                        formatValue={(v) => `${v} days`}
                        description="How long to keep your creative data and session history"
                    />

                    <ToggleSetting
                        label="Crystallization Consent"
                        checked={privacy.crystallization_consent}
                        onChange={(v) => updatePrivacy('crystallization_consent', v)}
                        description="When enabled, your creative decisions are crystallized into your Codex, making your seity progressively better at understanding your creative voice."
                    />
                </SettingsSection>
            )}

            {/* Danger Zone */}
            <SettingsSection title="Danger Zone" defaultExpanded={false}>
                <div className="space-y-3">
                    <DisabledDangerItem
                        label="Clear Cache"
                        reason="Not yet available. Your creative work is not affected by cached data."
                    />
                    <DisabledDangerItem
                        label="Export All Data"
                        reason="Not yet available. Use Backup above to download a portable copy of your data."
                    />
                    {deletionStatus?.status !== 'completed' && deletionStatus?.status !== 'executing' && (
                        <div className="space-y-2">
                            <p className="text-xs" style={{ color: 'var(--sp-text-muted)' }}>
                                Before deleting your account, download your data or choose Skip.
                            </p>
                            <div className="flex gap-2">
                                <button className="sp-focus-ring sp-button" disabled={deletionBusy} onClick={downloadBeforeDeletion}>
                                    Download my data
                                </button>
                                <button className="sp-focus-ring sp-button" disabled={deletionBusy} onClick={() => setExportChoice('skip')}>
                                    Skip
                                </button>
                            </div>
                            {exportChoice && (
                                <>
                                    <p className="text-xs" style={{ color: 'var(--sp-text-muted)' }}>
                                        {exportChoice === 'download' ? 'Download chosen.' : 'Download skipped.'}
                                    </p>
                                    <DangerButton
                                        label="Delete Account"
                                        confirmText="This will permanently delete your account and all associated data. This cannot be undone."
                                        confirmLabel="Delete My Account"
                                        requireTyping="DELETE"
                                        loading={deletionBusy}
                                        onConfirm={requestDeletion}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            </SettingsSection>
        </div>
    );
};

const DisabledDangerItem: React.FC<{ label: string; reason: string }> = ({ label, reason }) => (
    <div
        className="px-4 py-2 text-xs font-mono rounded opacity-50 cursor-not-allowed"
        style={{
            border: '1px solid var(--theme-surface-tertiary)',
            color: 'var(--theme-text-muted)',
        }}
    >
        <span>{label}</span>
        <span
            className="ml-2 text-[9px]"
            style={{ opacity: 0.7 }}
        >
            — {reason}
        </span>
    </div>
);
